import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaDownload, FaFileAlt, FaChartLine, FaArrowRight, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaInfoCircle, FaCubes } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const DownloadsModule5 = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [expandedReviewIndex, setExpandedReviewIndex] = useState(null);
  
  const toggleReview = (index) => {
    setExpandedReviewIndex(expandedReviewIndex === index ? null : index);
  };

  const reviews = [
    {
      version: "9.0.27",
      date: "25 de abril de 2023",
      changes: [
        "Mejoras en la compatibilidad con los formatos más recientes de Revit y ArchiCAD",
        "Optimización del proceso de extracción de cantidades para mejorar el rendimiento",
        "Nueva funcionalidad para la generación de reportes comparativos entre modelos",
        "Actualización de la interfaz para una experiencia más intuitiva"
      ]
    },
    {
      version: "9.0.24",
      date: "12 de marzo de 2023",
      changes: [
        "Soporte para nuevos formatos de archivos IFC y BCF",
        "Mejoras en el rendimiento al procesar modelos de gran tamaño",
        "Nuevas opciones de filtrado y selección de elementos en el modelo",
        "Optimización de la integración con el Módulo 1 para presupuestos"
      ]
    },
    {
      version: "9.0.21",
      date: "18 de enero de 2023",
      changes: [
        "Nueva funcionalidad para la asignación automática de conceptos",
        "Mejoras en la visualización 3D del modelo para identificación de elementos",
        "Correcciones en la sincronización bidireccional de datos",
        "Actualización de componentes internos para mayor compatibilidad y estabilidad"
      ]
    }
  ];

  const downloads = [
    {
      version: "12.0.0",
      date: "6 de abril de 2024",
      liteSize: "195 MB",
      fullSize: "720 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M5Revit_2022_12_20240406_Lite.zip",
      fullUrl: "https://www.ecosoft.com.mx/descargas/opus22/M5Revit_2022_12_20240406_Full.zip",
      hasFullVersion: true
    },
    {
      version: "11.0.0",
      date: "20 de junio de 2023",
      liteSize: "195 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M5Revit_2022_11_20230620_Lite.zip",
      hasFullVersion: false
    },
    {
      version: "6.0.0",
      date: "23 de septiembre de 2022",
      liteSize: "195 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M5Revit_2022_6_20220923_Lite.zip",
      hasFullVersion: false
    },
    {
      version: "3.0.0",
      date: "12 de julio de 2022",
      liteSize: "195 MB",
      fullSize: "720 MB",
      liteUrl: "https://www.ecosoft.com.mx/descargas/opus22/M5Revit_2022_3_20220712_Lite.zip",
      fullUrl: "https://www.ecosoft.com.mx/descargas/opus22/M5Revit_2022_3_20220712_Full.zip",
      hasFullVersion: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Descargas OPUS 22 Módulo 5 | Ecosoft"
        description="Descargue las últimas versiones de OPUS 22 Módulo 5: Cuantificación BIM para la integración con modelos 3D."
        name="DownloadsModule5"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[350px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl font-bold mb-2">OPUS 22 - Módulo 5</h1>
            <h2 className="text-2xl font-semibold mb-4">Cuantificación BIM</h2>
            <p className="text-lg mb-6">
              Descargue las últimas versiones del Módulo 5 de OPUS 22 para la integración con modelos BIM y cuantificación automatizada.
            </p>
            <Link to="/soporte/descargas" className="inline-flex items-center text-white hover:underline">
              <FaArrowRight className="mr-2 rotate-180" /> Volver al Centro de Descargas
            </Link>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                <FaInfoCircle className="text-[#0055B8] text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#1A2B39]">Información importante</h2>
                <p className="text-gray-600">Sobre las descargas de OPUS 22 - Módulo 5</p>
              </div>
            </div>
            
            <div className="mb-6 text-gray-700">
              <p className="mb-4">
                <strong>¿Qué revisión debo descargar?</strong>
              </p>
              <p className="mb-4">
                OPUS se adquiere bajo un esquema de suscripción, con fecha de caducidad. Esto quiere decir que puedes trabajar con revisiones con fecha de publicación igual o anterior a la fecha de caducidad de tu suscripción.
              </p>
              <p className="mb-4">
                La siguiente lista despliega todas las revisiones publicadas hasta el momento. Descarga e instala la revisión más reciente cuya fecha sea menor o igual a la fecha de caducidad de tu suscripción.
              </p>
              <p>
                El Módulo 5 de OPUS 22 te permite conectar tus presupuestos con modelos BIM, automatizando el proceso de cuantificación y manteniendo la sincronización entre el modelo y el presupuesto.
              </p>
            </div>
          </div>
          
          {/* Downloads Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Descargas disponibles</h2>
          
          <div className="mb-12">
            <div className="overflow-hidden rounded-lg shadow">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0055B8] text-white">
                    <th className="px-6 py-4 text-left">Revisión</th>
                    <th className="px-6 py-4 text-left">Fecha</th>
                    <th className="px-6 py-4 text-left">Descargas</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {downloads.map((download, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">
                        {download.version}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {download.date}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-3">
                          <a 
                            href={download.liteUrl} 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-[#0055B8] text-white rounded hover:bg-[#003a80] transition-colors"
                          >
                            <FaDownload className="mr-2" /> Descarga Lite ({download.liteSize})
                          </a>
                          
                          {download.hasFullVersion && (
                            <a 
                              href={download.fullUrl} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-[#1A2B39] text-white rounded hover:bg-[#0d1824] transition-colors"
                            >
                              <FaDownload className="mr-2" /> Descarga Completa ({download.fullSize})
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mt-3">
              * Descarga e instala el paquete Lite si ya tienes instalado OPUS MÓDULO 5 en versión 21 ó la versión de lanzamiento de OPUS22.
            </p>
          </div>
          
          {/* Changes and Improvements Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Cambios y mejoras</h2>
          
          <div className="space-y-4 mb-12">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg shadow">
                <div 
                  className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleReview(index)}
                >
                  <div>
                    <h3 className="font-bold text-lg text-[#1A2B39]">Revisión {review.version}</h3>
                    <p className="text-gray-600">Fecha: {review.date}</p>
                  </div>
                  <div>
                    {expandedReviewIndex === index ? (
                      <FaChevronUp className="text-[#0055B8]" />
                    ) : (
                      <FaChevronDown className="text-[#0055B8]" />
                    )}
                  </div>
                </div>
                
                {expandedReviewIndex === index && (
                  <div className="p-4 pt-0 border-t border-gray-200">
                    <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-3">
                      {review.changes.map((change, changeIndex) => (
                        <li key={changeIndex}>{change}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Features Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Características principales</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Integración con software BIM</h3>
              <p className="mb-4 text-gray-600">
                Conecta OPUS con los principales programas BIM del mercado, como Revit, ArchiCAD, Tekla y más, utilizando formatos estándar como IFC.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Visualización 3D</h3>
              <p className="mb-4 text-gray-600">
                Visualiza el modelo 3D directamente en OPUS, con opciones para navegar, seleccionar elementos y filtrar la visualización por categorías.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Cuantificación automatizada</h3>
              <p className="mb-4 text-gray-600">
                Obtén cantidades automáticamente a partir de los elementos del modelo, con reglas de extracción configurables según tus necesidades.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Sincronización bidireccional</h3>
              <p className="mb-4 text-gray-600">
                Mantén la sincronización entre el presupuesto y el modelo BIM, con actualización automática de cantidades cuando el modelo cambia.
              </p>
            </div>
          </div>
          
          {/* Requirements Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Requisitos del sistema</h2>
          
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200 mb-12">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Requisitos mínimos</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Procesador Intel Core i5 o AMD Ryzen 5 (o superior)</li>
                  <li>8 GB de RAM (16 GB recomendado)</li>
                  <li>500 MB de espacio disponible en disco duro</li>
                  <li>Tarjeta gráfica compatible con DirectX 11 o superior</li>
                  <li>Windows 10 o 11 (64 bits)</li>
                  <li>Microsoft .NET Framework 4.7 o superior</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Software compatible</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Autodesk Revit 2019 o posterior</li>
                  <li>ArchiCAD 21 o posterior</li>
                  <li>Tekla Structures 2019 o posterior</li>
                  <li>Cualquier software que exporte en formato IFC 2x3 o 4</li>
                  <li>OPUS 22 Módulo 1 (Presupuesto Programable)</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Additional Resources */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Recursos adicionales</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Guías y manuales</h3>
              <p className="mb-4 text-gray-600">
                Para obtener un manual actualizado, envíanos un correo a sgarcia@ecosoft.com.mx desde tu cuenta de correo asociada a tu suscripción vigente.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Soporte técnico</h3>
              <p className="mb-4 text-gray-600">
                Si necesitas ayuda con la instalación o uso de OPUS, ponte en contacto con nuestro equipo de soporte técnico.
              </p>
              <p className="text-[#0055B8] font-bold">
                WhatsApp Soporte: (55) 7965.8522
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadsModule5; 